import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Crown } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { hasNativeBridge, isStandalonePWA } from "@/lib/device";

const CELEBRATED_KEY = "rc_install_celebrated";
const GOLDS = ["#D4AF37", "#F2D272", "#C9A227", "#E7C558", "#FFE9A8", "#B8860B"];

// A burst of gold confetti + a royal "Installed" toast-card. Fires on:
//  1) browser PWA install (window 'appinstalled')
//  2) first launch of the installed PWA / native (Median) app — once, persisted.
function ConfettiBurst() {
  const pieces = useMemo(() => {
    const w = typeof window !== "undefined" ? window.innerWidth : 390;
    return Array.from({ length: 90 }).map((_, i) => {
      const angle = (Math.PI * (0.15 + Math.random() * 0.7)); // upward-ish fan
      const speed = 220 + Math.random() * 520;
      const dir = Math.random() > 0.5 ? 1 : -1;
      return {
        id: i,
        startX: w / 2 + (Math.random() - 0.5) * 120,
        dx: dir * Math.cos(angle) * speed,
        dy: -Math.sin(angle) * speed - 120,
        rot: (Math.random() - 0.5) * 720,
        size: 7 + Math.random() * 9,
        color: GOLDS[i % GOLDS.length],
        delay: Math.random() * 0.18,
        round: Math.random() > 0.6,
        dur: 1.6 + Math.random() * 1.1,
      };
    });
  }, []);

  return (
    <div className="pointer-events-none fixed inset-0 z-[100] overflow-hidden" data-testid="install-confetti">
      {pieces.map((p) => (
        <motion.span
          key={p.id}
          initial={{ x: p.startX, y: window.innerHeight * 0.42, opacity: 1, rotate: 0 }}
          animate={{
            x: p.startX + p.dx,
            y: window.innerHeight * 0.42 + p.dy + window.innerHeight * 0.9,
            opacity: [1, 1, 0],
            rotate: p.rot,
          }}
          transition={{ duration: p.dur, delay: p.delay, ease: [0.15, 0.6, 0.4, 1] }}
          style={{
            position: "absolute",
            width: p.size,
            height: p.round ? p.size : p.size * 0.5,
            borderRadius: p.round ? "9999px" : "2px",
            background: p.color,
            boxShadow: `0 0 6px ${p.color}88`,
            left: 0,
            top: 0,
          }}
        />
      ))}
    </div>
  );
}

export default function InstallCelebration() {
  const { lang } = useI18n();
  const fr = lang === "fr";
  const [show, setShow] = useState(false);

  const fire = () => {
    try { localStorage.setItem(CELEBRATED_KEY, "1"); } catch (e) {}
    setShow(true);
    if (navigator.vibrate) { try { navigator.vibrate([30, 40, 30]); } catch (e) {} }
    setTimeout(() => setShow(false), 3200);
  };

  useEffect(() => {
    const already = (() => { try { return localStorage.getItem(CELEBRATED_KEY) === "1"; } catch (e) { return false; } })();

    // 1) Browser install event (Android Chrome / desktop PWA)
    const onInstalled = () => { if (localStorage.getItem(CELEBRATED_KEY) !== "1") fire(); };
    window.addEventListener("appinstalled", onInstalled);

    // 2) First launch of an installed/native app — no install event exists inside
    //    the Median shell or a home-screen PWA, so celebrate once on first open.
    if (!already && (hasNativeBridge() || isStandalonePWA())) {
      const t = setTimeout(fire, 700);
      return () => { window.removeEventListener("appinstalled", onInstalled); clearTimeout(t); };
    }
    return () => window.removeEventListener("appinstalled", onInstalled);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AnimatePresence>
      {show && (
        <>
          <ConfettiBurst />
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 260, damping: 22 }}
            className="pointer-events-none fixed left-1/2 top-[38%] z-[101] -translate-x-1/2"
            data-testid="install-celebration-card"
          >
            <div className="flex flex-col items-center gap-3 rounded-3xl border border-[var(--rc-gold)]/30 bg-[var(--rc-surface)]/95 px-8 py-6 text-center shadow-[0_20px_60px_-10px_rgba(212,175,55,0.35)] backdrop-blur-md">
              <motion.span
                initial={{ scale: 0, rotate: -30 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 300, damping: 12, delay: 0.1 }}
                className="grid h-16 w-16 place-items-center rounded-full bg-[var(--rc-gold)] text-black shadow-[0_0_0_6px_var(--rc-bg)]"
              >
                <Crown size={30} />
              </motion.span>
              <div className="font-heading text-2xl text-[var(--rc-gold)]">
                {fr ? "RoyalChat Nova est installé\u00a0!" : "RoyalChat Nova installed!"}
              </div>
              <p className="max-w-[15rem] text-sm text-[var(--rc-muted)]">
                {fr ? "Bienvenue dans la famille royale\u00a0\ud83d\udc51" : "Welcome to the royal family \ud83d\udc51"}
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
